import type { AttributeResponse, JSONQuery, Suggestion, SuggestResponse } from "../types/suggestion";
import type { TrackingData } from "../types/tracking";

/*                                                         */
/*         */

import { headlineTemplate, itemLabelTemplate, keywordsTemplate, modifiedArrowTemplate } from "./suggestions.templates";
import { util } from "../helper/util";
import { suggestTracking } from "../tracking";
import {
  ELEMENT_EVENT,
  ErrorMessage,
  FALLBACK_SEARCH_URL,
  SEMANTIC_TRACKING_STRING,
  SEMANTIC_TRACKING_VALUE,
  TRACK_LABEL,
} from "../types/constants";
import { Queue } from "./PromiseQueue";
import { EmptyStateTime, InitSearchFieldTime, SuggestRenderTime, SuggestTime } from "../times";
import { SquirrelURL } from "../helper";
import { SqrlEvent } from "../events";
import { CLASSNAME, DATA, KEY_CODE, REPTILE, SELECTOR } from "../types/element/constants";
import { COOKIE } from "../types/cookie/constants";
import { PLACEHOLDER } from "../types/label";
import { suggestLayer } from "../helper/SquirrelElement";

const Mustache = window.Mustache;
const templateString: string = `<div class="squirrel_searchSuggestions pl_table-view"><ul class="squirrel_searchSuggestions__list squirrel_border_top">{{>keywordTemplate}}{{>footerLiTemplate}}</ul></div>`;

let headerElement: Element | null;
let searchbarWrapper: HTMLDivElement | null;
let searchForm: HTMLFormElement | null;
let searchField: HTMLInputElement | null;
let oldSearchTerm: string | undefined | null;

let isEmptyState: boolean;
let sendTrackingLabelOnFocus = true;
let sendSemanticOpportunityTracking = !Boolean(document.getElementById(REPTILE.SEARCH_RESULT_ID)).valueOf();

/**
 *
 *
 */
export function setOldSearchTerm(string: string) {
  oldSearchTerm = string;
}

export function focusOutSearchFieldAction() {
  if (searchField?.value == "") {
    EmptyStateTime.measure();
    /*                                                                                   */
    setSendSemanticOpportunityTracking(true);
  }
  setSendTrackingLabelOnFocus(true);
}

export function clickSearchFieldAction() {
  if (!searchField) {
    throw new Error("searchSuggest.clickSearchFieldAction: searchField is not defined");
  }
  if (suggestLayer().innerHTML.length == 0 && searchField.value === "") {
    window.o_squirrel.emptyStateSuggest.show();
  }
}

/**
 *
 *
 *
 */
export async function focusSearchFieldAction(evt: Event) {
  if (!headerElement) {
    throw new Error("searchSuggest.focusSearchFieldAction: headerElement is not defined");
  }

  SuggestTime.reset();
  SuggestRenderTime.reset();
  const sessionStart = Number(window.o_util.cookie.get(COOKIE.SESSION_START));
  InitSearchFieldTime.measureOnce(sessionStart);

  const selectedSearchField: HTMLInputElement = evt.target as HTMLInputElement;
  const value = selectedSearchField.value;
  const searchTerm: string = util.string.trimLeft(value);
  const clickPosition: number | null = selectedSearchField.selectionStart;
  if (searchTerm !== "") {
    if (suggestLayer().innerHTML !== "" && !!oldSearchTerm) {
      await suggest(searchTerm, clickPosition);
    }
  } else {
    window.o_squirrel.emptyStateSuggest.show(() => {
      if (searchTerm == "" && sendTrackingLabelOnFocus) {
        SqrlEvent.createFocusSearchFieldEventContext();
        setSendTrackingLabelOnFocus(false);
        window.o_squirrel.navSlider.updateHTMLTemplateOnce();
      }
    });
  }
}

function blurWindowAction() {
  if (document.activeElement == searchField) {
    setSendTrackingLabelOnFocus(false);
  }
}

function setSendTrackingLabelOnFocus(flag: boolean) {
  sendTrackingLabelOnFocus = flag;
}

function getSendTrackingLabelOnFocus(): boolean {
  return sendTrackingLabelOnFocus;
}

function setSendSemanticOpportunityTracking(flag: boolean) {
  sendSemanticOpportunityTracking = flag;
}

/**
 *
 *
 *
 */
export function clickArrowAction(this: Element, evt: Event) {
  if (!searchField) {
    throw new Error("searchSuggest.clickArrowAction: searchField is not defined");
  }
  if (!searchForm) {
    throw new Error("searchSuggest.clickArrowAction: searchForm is not defined");
  }

  const elem: HTMLElement = evt.target as HTMLElement;
  const target: ParentNode = (Boolean(this.parentNode).valueOf() ? this.parentNode : elem.offsetParent) as ParentNode; /*                         */
  const term: HTMLSpanElement = target.querySelector(SELECTOR.SEARCH_TERM) as HTMLSpanElement;
  const searchTerm: string = term.getAttribute(DATA.TERM) || "";
  const position: string = term.getAttribute(DATA.POS) || "";

  window.o_util.event.stop(evt);

  setSuggest(position);
  replaceSearchTermAndSetDataAttribute(term.getAttribute(DATA.TERM) + " ");
  SqrlEvent.sendSuggestEventToTracking(searchTerm, position);

  suggestTracking.unmarkFormForTracking(CLASSNAME.TRACK_SUGGEST, searchForm);
  suggestTracking.markFormForTracking(CLASSNAME.TRACK_MODIFIED, searchForm);

  searchField.focus();
}

/**
 *
 *
 */
export function hideLayerEventHandler(evt: MouseEvent) {
  const elem: HTMLElement = evt.target as HTMLElement;
  if (!elem.closest(SELECTOR.SEARCHBAR_WRAPPER) && !elem.classList.contains(CLASSNAME.IGNORE_SUGGEST_LIST)) {
    hideLayer();
  }
}

/**
 *
 *
 */
export function updateSuggestionLayer<E extends HTMLDivElement>(this: E, response: string | SuggestResponse): E {
  /*                                                                                                                           */
  /*                                                                                                         */
  /*                                                                                         */
  SuggestRenderTime.measure();
  if (typeof response === "string") {
    const suggestionsTemplate = response;
    this.innerHTML = suggestionsTemplate;
  } else {
    const sl = document.querySelector(SELECTOR.LAYER) as HTMLDivElement;
    const suggestResponse = response;
    suggestResponse.keywords.forEach((s, i) => {
      const span = sl.querySelector(`[data-suggestpos="${i + 1}"]`) as HTMLSpanElement;
      span.innerHTML = s.visual;
      span.setAttribute(DATA.TERM, s.term);
      span.setAttribute(DATA.TARGET, s.target);
    });
  }
  return this;
}

/**
 *
 *
 *
 */
export function onSuggestItemClick(this: Element, evt: Event) {
  if (!searchForm) {
    throw new Error("searchSuggest.onSuggestItemClick: searchForm is not defined");
  }
  if (!searchField) {
    throw new Error("searchSuggest.onSuggestItemClick: searchField is not defined");
  }

  const elem: HTMLElement = evt.target as HTMLElement;
  const target: ParentNode = (Boolean(this.querySelector).valueOf() ? this : elem.offsetParent) as ParentNode;
  const term: HTMLDivElement = target.querySelector(SELECTOR.SEARCH_TERM) as HTMLDivElement;

  window.o_util.event.stop(evt);

  setSuggest(term.getAttribute(DATA.POS));
  const originalSearchTerm: AttributeResponse = searchField.getAttribute(DATA.SUGGEST_SEARCH_TERM);
  isEmptyState = searchField.value == "";
  replaceSearchTermAndSetDataAttribute(term.getAttribute(DATA.TERM));
  let suggestTime: number | void = undefined;
  try {
    suggestTime = SuggestTime.measure();
  } catch (e) {
    /*                 */
  }
  const searchTermElement: HTMLElement = this.querySelector(SELECTOR.SEARCH_TERM) as HTMLElement;
  const targetUrl: string = searchTermElement.getAttribute(DATA.TARGET) as string;
  const urlObject = new SquirrelURL(targetUrl);
  const hashTrackingParameters: TrackingData = urlObject.trackingParameter;

  const trackingParameters: TrackingData = {};
  const isSuggest = !!originalSearchTerm;
  if (isSuggest && suggestTime) {
    trackingParameters[TRACK_LABEL.WK_SUGGEST_TIME] = suggestTime;
  }

  suggestTracking.trackUsage(trackingParameters, hashTrackingParameters, false, true, isEmptyState, false);

  suggestTracking.unmarkFormForTracking(CLASSNAME.TRACK_MODIFIED, searchForm);
  window.o_squirrel.submitHistory.addSearch(searchField.value);
  submitForm(urlObject.urlWithoutTrackingParameter);
}

/**
 *
 *
 *
 *
 *
 *
 */
export function retrieveSuggestions(
  searchTerm: string,
  clickPosition: number | null,
): Promise<string | SuggestResponse> {
  return fetchSuggestions(searchTerm, clickPosition, {})
    .then(extractSuggestions)
    .then(updateSearchFieldTarget)
    .then(maybeTrackSemantic)
    .then(renderSuggestions);
}

function maybeTrackSemantic(response: SuggestResponse | ErrorMessage | Promise<ErrorMessage>) {
  /*                                                                                        */
  const suggestResponse = response as SuggestResponse;

  if (!sendSemanticOpportunityTracking) {
    return suggestResponse;
  }

  const semanticExists = suggestResponse.keywords.some((suggestion) =>
    suggestion.target.includes(SEMANTIC_TRACKING_STRING),
  );

  if (semanticExists) {
    SqrlEvent.sendSemanticEventToTracking(SEMANTIC_TRACKING_VALUE.SEMANTIC);
    setSendSemanticOpportunityTracking(false);
  }

  return suggestResponse;
}

/**
 *
 *
 *
 *
 */
export function fetchSuggestions(
  searchTerm: string,
  c: number | null,
  requestInit: RequestInit = {},
): Promise<SuggestResponse> {
  const url: string | null = suggestLayer().getAttribute(DATA.SUGGEST_URL);
  if (!url) {
    throw new Error("searchSuggest.fetchSuggestions: undefined suggest url");
  }
  const q: string = searchTerm;
  let history = "";
  if (!window.o_squirrel.submitHistory.isEmpty()) {
    history = window.o_squirrel.submitHistory.encode();
  }
  const clickPosition: object | undefined = c === null ? undefined : { c };
  const historyParameter: object | undefined =
    window.o_squirrel.personlizationEnabled && history.length > 0 ? { history } : undefined;
  const parameter: JSONQuery = Object.assign({ q }, historyParameter, clickPosition);

  return fetch(SquirrelURL.encode(url, parameter), requestInit).then((response: Response) => {
    return response.json();
  });
}

export function extractSuggestions(response: SuggestResponse): SuggestResponse {
  return {
    original: response.original,
    keywords: response.keywords,
    suggestPos: util.core.counter(),
  };
}

export function updateSearchFieldTarget(
  response: SuggestResponse,
): SuggestResponse | ErrorMessage | Promise<ErrorMessage> {
  if (!searchField) {
    throw new Error("searchSuggest.extractSuggestions: searchField is not defined");
  }

  if (response && response.original && response.original.target) {
    const original: Suggestion = response.original;
    searchField.setAttribute(DATA.TARGET, original.target);
    searchField.setAttribute(DATA.TARGET_TERM, original.term);
  }

  /*                                                                           */
  if (!response) {
    return Promise.reject(ErrorMessage.NO_OK_SUGGEST_RESPONSE);
  } else if (!response.keywords) {
    return Promise.reject(ErrorMessage.KEYWORDS_UNDEFINED);
  } else if (response.keywords.length === 0) {
    return Promise.reject(ErrorMessage.EMPTY_SUGGESTIONS);
  }
  return response;
}

function renderKeywordTemplate(suggestions: SuggestResponse): string {
  return Mustache.render(keywordsTemplate, suggestions, {
    itemLabelTemplate,
    modifiedArrowTemplate,
  });
}

/**
 *
 *
 *
 */
export function renderSuggestions(results: SuggestResponse | ErrorMessage): string | SuggestResponse {
  if (!searchField) {
    throw new Error("searchSuggest.renderSuggestions: searchField is not defined");
  }
  const response: SuggestResponse = results as SuggestResponse;
  if (
    !!suggestLayer() &&
    oldSearchTerm &&
    suggestLayer().querySelectorAll(SELECTOR.SEARCH_SUGGEST).length == response.keywords?.length &&
    !suggestLayer().querySelector(SELECTOR.SUGGESTIONS_HEADLINE)?.innerHTML.includes(PLACEHOLDER)
  ) {
    return response;
  }

  return Mustache.render(
    templateString,
    {},
    {
      headlineTemplate,
      keywordTemplate: renderKeywordTemplate(response),
    },
  );
}

/**
 *
 */
export function closeLayer() {
  SqrlEvent.close();
  hideLayer();
}

/**
 *
 */
export function showLayer(): HTMLFormElement {
  if (!searchForm) {
    throw new Error("searchSuggest.showLayer: suggestForm is not defined");
  }
  if (!searchbarWrapper) {
    throw new Error("searchSuggest.showLayer: searchWrapper is not defined");
  }

  document.body.addEventListener(ELEMENT_EVENT.CLICK, hideLayerEventHandler);
  searchbarWrapper.classList.add(CLASSNAME.SEARCHBAR_WRAPPER_VISIBLE_SUGGESTIONS);
  suggestLayer().style.display = "block";
  return searchForm;
}

/**
 *
 */
export function submitForm(action: string) {
  util.global.setLocation(action);
}

/**
 *
 *
 *
 *
 */
export function setSuggest(suggestPos?: string | number | undefined | null, redirectTarget?: string | null) {
  if (!searchForm) {
    throw new Error("searchSuggest.setSuggest: searchForm is not defined");
  }

  searchForm.setAttribute(DATA.POS, suggestPos ? (suggestPos as string) : "");
  if (redirectTarget) {
    searchForm.setAttribute(DATA.TARGET, redirectTarget);
  } else {
    searchForm.removeAttribute(DATA.TARGET);
  }
}

/**
 *
 *
 *
 */
export function replaceSearchTerm(searchTerm: string | null) {
  if (!searchField) {
    throw new Error("searchSuggest.replaceSearchTerm: searchField is not defined");
  }
  if (searchTerm !== "" && searchTerm !== null) {
    searchField.value = searchTerm;
    /*                                                                     */
    searchField.setAttribute(DATA.TARGET_TERM, searchTerm);
  } else {
    /*                                                                                                               */
    searchField.removeAttribute(DATA.TARGET_TERM);
  }
}

export function replaceSearchFieldTarget(searchTerm: string | null, target: string | null) {
  if (!searchField) {
    throw new Error("searchSuggest.replaceSearchTerm: searchField is not defined");
  }
  if (target == null && searchTerm !== "" && searchTerm !== null) {
    target = FALLBACK_SEARCH_URL(searchTerm, { [TRACK_LABEL.TARGET_URL_FALLBACK]: "replace" });
  }
  if (target) {
    searchField.setAttribute(DATA.TARGET, target);
  }
}

/**
 *
 *
 *
 */
export function replaceSearchTermAndSetDataAttribute(searchTerm: string | null) {
  if (!searchField) {
    throw new Error("searchSuggest.replaceSearchTermAndSetDataAttribute: searchField is not defined");
  }
  if (searchTerm !== "" && searchTerm !== null) {
    searchField.value = searchTerm;
    searchField.setAttribute(DATA.SUGGEST_SEARCH_TERM, util.string.trimRight(searchTerm));
  }
}

/**
 *
 */
export async function suggest(searchTerm: string, clickPosition: number | null): Promise<void> {
  isEmptyState = searchTerm == "";
  return Queue.enqueue(() =>
    retrieveSuggestions(searchTerm, clickPosition)
      .then(updateSuggestionLayer.bind(suggestLayer()))
      .then(() => setSuggest(-1))
      .then(showLayer)
      .then(scrollToSearchField)
      .catch(handleError),
  );
}

export function scrollToSearchField() {
  const searchIcon = document.querySelector(SELECTOR.SEARCH_ICON) as HTMLDivElement;
  const searchIconIsDisplayed = window.getComputedStyle(searchIcon).display;

  const itunesMetaElement = document.querySelector('meta[name="apple-itunes-app"]');
  if (searchIconIsDisplayed !== "none" && itunesMetaElement == null) {
    const container: HTMLDivElement = document.querySelector(SELECTOR.SEARCH_CONTAINER) as HTMLDivElement;
    const top: number = container.getBoundingClientRect().top + window.scrollY - 7;
    const behavior: ScrollBehavior = "smooth";
    window.scroll({ top, behavior });
  }
}

/**
 *
 *
 */
export function updateSearchField(activeElement: Element) {
  if (!searchForm) {
    throw new Error("searchSuggest.updateSearchField: searchForm is not defined");
  }
  const termElement: HTMLElement = activeElement.querySelector(SELECTOR.SEARCH_TERM) as HTMLElement;
  const redirectTarget: AttributeResponse = termElement.getAttribute(DATA.TARGET);
  const term: AttributeResponse = termElement
    ? termElement.getAttribute(DATA.TERM)
    : activeElement.getAttribute(DATA.TERM);
  if (Boolean(termElement).valueOf()) {
    setSuggest(termElement.getAttribute(DATA.POS), redirectTarget);
  }
  replaceSearchTerm(term);
  replaceSearchFieldTarget(term, redirectTarget);
  suggestTracking.markFormForTracking(CLASSNAME.TRACK_SUGGEST, searchForm);
}

/**
 *
 *
 *
 */
export function updateActiveElement(previous: HTMLLIElement | null, next: Element | null) {
  if (previous) {
    previous.classList.remove(CLASSNAME.SELECTED_SEARCH_SUGGEST);
  }
  if (next) {
    next.classList.add(CLASSNAME.SELECTED_SEARCH_SUGGEST);
    updateSearchField(next);
  }
}

/**
 *
 *
 */
export function getInitiallyActiveElement(key: string): HTMLLIElement | null {
  const entries: NodeListOf<HTMLLIElement> = suggestLayer().querySelectorAll(SELECTOR.SEARCH_SUGGEST);
  switch (key) {
    case KEY_CODE.ARROW_DOWN:
      return entries[0];
    case KEY_CODE.ARROW_UP:
      return entries[entries.length - 1];
    default:
      return null;
  }
}

export function onFirstChar() {
  if (!searchField) {
    throw new Error("searchSuggest.onFirstChar: searchField is not defined");
  }

  if (!oldSearchTerm && !!searchField.value) {
    SuggestTime.reset();
    EmptyStateTime.measure();
  }
}

/**
 *
 *
 *
 */
export function getNextActiveElement(key: string, current: HTMLLIElement): Element | null {
  if (!current) {
    return null;
  }

  switch (key) {
    case KEY_CODE.ARROW_DOWN:
      return current.nextElementSibling as HTMLLIElement;
    case KEY_CODE.ARROW_UP:
      return current.previousElementSibling as HTMLLIElement;
    default:
      return null;
  }
}

export function handleKeyAction(evt: KeyboardEvent) {
  let activeElement: HTMLLIElement;
  const code: string = Boolean(evt.which).valueOf() ? evt.which + "" : evt.code;
  switch (code) {
    case KEY_CODE.ARROW_UP:
    case KEY_CODE.ARROW_DOWN:
      evt.preventDefault();
      activeElement = suggestLayer().querySelector(SELECTOR.SELECTED_SEARCH_SUGGEST) as HTMLLIElement;
      updateActiveElement(
        activeElement,
        activeElement ? getNextActiveElement(code, activeElement) : getInitiallyActiveElement(code),
      );
      break;
    case KEY_CODE.ESC:
      closeLayer();
      break;
  }
}

export async function handleInput() {
  if (!searchForm) {
    throw new Error("searchSuggest.handleKeyAction: searchForm is not defined");
  }
  if (!searchField) {
    throw new Error("searchSuggest.handleKeyAction: searchField is not defined");
  }

  SuggestRenderTime.reset();
  onFirstChar();
  const searchTerm = util.string.trimLeft(searchField.value);
  const clickPosition = searchField.selectionStart;
  searchField.setAttribute(DATA.SUGGEST_SEARCH_TERM, searchTerm);

  if (searchTerm === "") {
    window.o_squirrel.emptyStateSuggest.show();
  }

  if (searchTerm !== "") {
    if (oldSearchTerm !== searchTerm) {
      if (!oldSearchTerm) {
        SqrlEvent.sendCharEventToTracking();
      }
      await window.o_squirrel.searchSuggest.suggest(searchTerm, clickPosition);
    }
    suggestTracking.unmarkFormForTracking(CLASSNAME.TRACK_SUGGEST, searchForm!);
  }

  window.o_squirrel.searchSuggest.setOldSearchTerm(searchTerm);
}

/**
 *
 */
/*                                                         */
export function handleError(_e: ErrorMessage) {
  hideLayer();
  /*                                                                          */
  /*                                                                             */
  /*       */
}

/**
 *
 */
export function hideLayer() {
  if (!searchbarWrapper) {
    throw new Error("searchSuggest.hideLayer: searchbarWrapper is not defined");
  }
  window.o_squirrel.searchHistory.clear();
  document.body.removeEventListener(ELEMENT_EVENT.CLICK, hideLayerEventHandler);
  searchbarWrapper.classList.remove(CLASSNAME.SEARCHBAR_WRAPPER_VISIBLE_SUGGESTIONS);
  suggestLayer().style.display = "none";
  suggestLayer().innerHTML = "";
  /*                         */
}

/**
 *
 */
export function reset() {
  if (!searchField) {
    throw new Error("searchSuggest.reset: searchField is not defined");
  }
  if (!searchbarWrapper) {
    throw new Error("searchSuggest.reset: searchbarWrapper is not defined");
  }
  searchField.value = "";
  hideLayer();
  searchbarWrapper = null;
  searchForm = null;
  searchField = null;
  oldSearchTerm = "";
}
/*                       */
export function setChristmasPlaceholder() {
  if (!searchField) {
    throw new Error("searchSuggest.clickSearchFieldAction: searchField is not defined");
  }

  const christmasDate: unknown = window.o_squirrel.christmasDate;
  const fromDate: unknown = new Date();
  const diffTime = (christmasDate as number) - (fromDate as number);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays >= 0) {
    searchField.setAttribute("placeholder", "Ho ho ho, frohe Suche!");
  } else {
    searchField.setAttribute("placeholder", "Wonach suchst du?");
  }
}

export function init() {
  const parentWrapper: HTMLDivElement = document.querySelector(SELECTOR.SEARCH_CONTAINER) as HTMLDivElement;

  if (!searchForm && parentWrapper) {
    searchbarWrapper = parentWrapper.querySelector(SELECTOR.SEARCH_WRAPPER) as HTMLDivElement;
    searchForm = parentWrapper.querySelector(SELECTOR.SEARCH_FORM) as HTMLFormElement;
    searchField = parentWrapper.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
    headerElement = document.querySelector(SELECTOR.HEADER_ELEMENT) as HTMLElement;
    /*                       */
    setChristmasPlaceholder();
  }
}

export const searchSuggest = {
  init,
  reset,
  setOldSearchTerm,
  hideLayer,
  handleError,
  showLayer,
  updateSuggestionLayer,
  suggest,
  fetchSuggestions,
  extractSuggestions,
  updateSearchFieldTarget,
  scrollToSearchField,
  setSuggest,
  onSuggestItemClick,
  clickArrowAction,
  handleKeyAction,
  handleInput,
  clickSearchFieldAction,
  focusSearchFieldAction,
  focusOutSearchFieldAction,
  blurWindowAction,
  getSendTrackingLabelOnFocus,
  setSendTrackingLabelOnFocus,
  setSendSemanticOpportunityTracking,
  maybeTrackSemantic,
};
